import React from 'react';
import classes from 'classnames';
import { computed, observable, action, reaction, runInAction, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { DefaultButton, Checkbox, mergeStyleSets } from '@fluentui/react';

import SearchableComboBox from 'src/components/base/SearchableComboBox';
import GroupPane from 'src/components/base/GroupPane';
import { ModalPage } from 'src/components/base/Page';
import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';
import { CheckboxTreeListWithSearch, makeId, updateItems, getItem, getItems } from 'src/components/base/TreeList';
import ButtonTooltip from '@v2/components/base/ButtonTooltip';

import FilterManager, { createFilterNode } from 'src/components/Flowsteps/base/FilterManager';
import { Button, } from 'src/components/Flowsteps/base/Flowstep';
import Step from 'src/components/Flowsteps/base/Step';
import PullFlowstep from 'src/components/Flowsteps/PullData/PullFlowstep';

import { Type, generateFlowstepName, isInsertKind } from 'shared/utils/flowstep';
import { substituteQueryWithFakes } from 'shared/utils/querySubstitution';
import {
  ParsingOption, RelationshipType,
  toFullLabel, toFullName,
  isRelationshipField, isPolymorphicField,
  isFieldsFunction
} from 'shared/utils/queryAnalysis';
import { runShortAction } from 'src/services/utils';
import { includesParttern } from 'shared/utils/utils';
import { links } from 'src/constants/index';



const classNames = mergeStyleSets({
  codemirror: {
    border: '1px solid #A6A6A6',
    opacity: 0.8,
    margin: '16px 16px 0px 16px',
  },
  block: {
    margin: 16,
  },
  buttonBlock: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'stretch',
    margin: '16px 0px 0px -16px',
    selectors: {
      '& > *': {
        flexGrow: 1,
        marginLeft: 16,
      },
    },
  },
  options: {
    margin: '16px 0px 0px 0px',
  },
  optionsContent: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
      },
    },
  },
  optionsContentV: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 14,
      },
    },
  },

  fieldText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    textAlign: 'center',
  },
  fieldTextInactive: {
    color: 'gray',
  },

  contentWithouGroupPane: {
    margin: '0 16px',
    selectors: {
      '& > div': {
        marginTop: 14,
      }
    }
  }
});


const Data = {
  Buttons: {
    [Button.Run]: { text: 'Run query', tooltip: 'Executes the specified query without saving it inside of the worksheet as a flow step' },
    [Button.RunAndSave]: { tooltip: 'Executes the specified query and saves it inside of the worksheet as a flow step' },
    [Button.Save]: { tooltip: 'Updates the query in the current flow step without executing it' },
  },
  MaxRelationshipDepth: 5,
  HelpLink: `https://www.xappex.com/docs/enabler4excel365/importing-data-from-salesforce-com-using-soql-salesforce-object-query-language/`,
};


@inject('salesforceStore', 'dataStore', 'officeStore', 'userStore')
export default class Soql extends PullFlowstep {
  @observable selectedObjectName = null;

  @observable showQueryPreview = false;
  @observable showFilterManager = false;
  @observable soql = {};

  @observable sourceFields = [];
  @observable selectedFieldsIds = [];
  @observable expandedFieldsIds = [];
  @observable fieldsFilter = '';

  @observable queryPreview = '';
  @observable isQueryPreviewValid = false;
  @observable query = '';
  @observable filters = [];


  constructor(props) {
    super(props, Type.Soql);
    makeObservable(this);

    this.options = {
      ...this.options,
      queryAll: false,
      showLabels: false,
    };

    const allReactions = [...super.setupBaseReactions(), reaction(() => this.query, () => this.isQueryPreviewValid = false)];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    return generateFlowstepName({ type: Type.Soql, sfObjectName: this.selectedObjectName });
  }

  @computed get _isValid_() {
    return super.areAllOptionsValid && (this.isValidQuery || this.isQueryPreviewValid);
  }

  @computed get _buttonsData_() { return Data.Buttons; }

  @computed get _helpArticleLink_() {
    return Data.HelpLink;
  }

  @computed get objects() {
    const selector = this.options.showLabels ? i => i.label : i => i.name;
    return (this.props ? this.props.officeStore.objects : []).map(i => ({ key: i.name, text: selector(i) }));
  }

  @computed get fields() {
    const mapFields = (fields) => {
      return fields.map(x => {
        const { fullName, fullLabel, rawField, referencesTo, isPolymorphicChild } = x;
        const { relationshipName, name, label } = rawField;

        const relationshipsTo = relationshipName
          ? ` (${relationshipName} to ${referencesTo.join(', ')})`
          : '';

        const relationship = relationshipName
          ? ` (${isPolymorphicChild ? referencesTo[0] : relationshipName})`
          : '';

        const fullDisplayName = this.options.showLabels ? fullLabel : fullName;
        const tooltip = fullDisplayName + relationshipsTo;
        const title = (this.options.showLabels ? label : name) + relationship;

        return ({
          ...x,
          tooltip, title, fullDisplayName,
          descendants: mapFields(x.descendants)
        });
      });
    }

    return mapFields(this.sourceFields);
  }

  @computed get fakeSubstitutedQuery() {
    return substituteQueryWithFakes(this.query, this.type);
  }

  @computed get isValidQuery() {
    return this.soql?.isQueryValid?.(this.fakeSubstitutedQuery.query);
  }


  async _componentDidMountStart_() {
    const task = this.fetchObjects();
    const soqlModule = await import(/* webpackChunkName: "soql" */'./soqlUtils');
    runInAction(() => {
      this.soql = soqlModule.default || soqlModule;
    });
    return task;
  }

  async _componentDidMountEnd_(task) {
    await task;
  }

  async _loadFlowstep_(flowstep, isExact) {
    super.loadFlowstep(flowstep, isExact);

    const { queryAll, showLabels = false, query = '', filters } = flowstep.options;
    this.options = { ...this.options, queryAll, showLabels };

    this.query = query;
    this.filters = filters;

    const { sourceFields, objectName, usedFields } = await this.parseAndLoadQuery(this.query);

    this.selectedObjectName = objectName;
    this.sourceFields = sourceFields;
    this.selectedFieldsIds = usedFields;
  }

  async _makeFlowstep_(makeCopy) {
    const { objectName, fields } = await this.prepareQuery();
    const sheetName = objectName || 'Sheet';
    const flowstep = await super.makeFlowstep(sheetName);

    Object.assign(flowstep.options, {
      query: this.query,
      object: { name: objectName },
      fields: fields.map(x => ({ name: x.name })),
      filters: this.filters,
      queryAll: this.options.queryAll,
      showLabels: this.options.showLabels,
    });

    return flowstep;
  }

  async _tryLoadFlowstepOfDifferentType_(allWithSameTarget, allSimilarTypesWithSameTarget) {
    const allInserts = allWithSameTarget.filter(x => isInsertKind(x.type));
    if (allInserts.length) {
      this.selectedObjectName = allInserts[0].options.mapping.sfObjectName;
    }
  }

  _renderBody_(renderProps) {
    const { getStepTitle } = renderProps;
    const { CodeMirror } = this.soql;

    // TODO: styles
    return (
      <>
        <div className={classNames.optionsContentV}>
          {this.renderCreateNewSheet(renderProps)}
        </div>

        <div className={classNames.optionsContentV}>
          <Step title={getStepTitle('Select the object(table) to get data from')} >
            <SearchableComboBox
              placeholder="Select object"
              selectedKey={this.selectedObjectName}
              onChange={async (event, item) => this.handleSelectObject(item.key)}
              options={this.objects}
              disabled={!this.objects.length || this._readOnly_}
            />
            <Checkbox label="Show labels"
              disabled={this._readOnly_}
              checked={this.options.showLabels}
              onChange={(_, checked) => this.handleOptionsChange('showLabels', checked)}
            />
          </Step>

          <Step title={getStepTitle('Select fields to return')} >
            <CheckboxTreeListWithSearch
              searchPlaceholder={'Start typing to filter'}
              items={this.fields}
              selectedIds={this.selectedFieldsIds}
              expandedIds={this.expandedFieldsIds}
              filter={this.fieldsFilter}
              disabled={this._readOnly_ || !this.sourceFields.length}
              onItemsSelected={this.handleFieldsSelected}
              onItemsCollapsed={this.handleFieldsCollapsed}
              onFilterChanged={this.handleFieldsFilterChanged}
              onFilterApplied={this.handleFieldsFilterApplied}
              onRenderItemTitle={this.handleRenderFieldTitle}
            />
          </Step>

          <Step title={getStepTitle('Setup filters (optional)')}>
            <DefaultButton
              text={`Filters manager (${this.filters.length})`}
              onClick={this.handleOpenFiltersManager}
              disabled={!this.sourceFields.length || this._readOnly_}
              primary={true}
            />
          </Step>

        </div>
        <GroupPane className={classNames.options} title='Edit Query' collapsed={true}>
          {CodeMirror &&
            <CodeMirror
              className={classNames.codemirror}
              value={this.query}
              options={{
                mode: 'sqlWithCustomKeywords',
                theme: 'default',
                lineNumbers: false,
                lineWrapping: true,
                placeholder: 'Enter a valid SOQL query text here or build one using the Query Builder controls above.',
                readOnly: this._readOnly_,
              }}
              onBeforeChange={(editor, data, value) => {
                this.query = value;
                this.setDirty();
              }}
            />
          }
        </GroupPane>




        <div className={classNames.contentWithouGroupPane}>
          {/* { this.renderErrorReaction(renderProps, { name: 'SOQL query', action: 'refreshed' }) } */}
          {this.renderPullModeCombobox(renderProps)}
        </div>

        {
          this.fakeSubstitutedQuery.hasSubstitutions &&
          <GroupPane className={classNames.options} title='Query Preview'>
            <div className={classNames.block}>
              <div className={classNames.buttonBlock}>
                <DefaultButton text='Calculate query' onClick={this.handlePreviewQuery} primary={true}
                // disabled={!this.isValidQuery}
                />
              </div>
            </div>

            {CodeMirror &&
              <CodeMirror
                className={classNames.codemirror}
                value={this.queryPreview}
                options={{
                  mode: 'sqlWithCustomKeywords',
                  theme: 'default',
                  lineNumbers: false,
                  lineWrapping: true,
                  placeholder: 'Query preview will be displayed here',
                  readOnly: this._readOnly_,
                }}
                onBeforeChange={(editor, data, value) => {
                  this.queryPreview = value;
                }}
              />
            }
          </GroupPane>
        }
      </>
    );
  }

  _renderCustomOptions_(renderProps) {
    return (
      <>
        {super._renderCustomOptions_(renderProps)}
        <CheckboxWithTooltip
          label={'Query all'}
          tooltip={'Check this box to include archived records in the query result'}
          disabled={this._readOnly_}
          checked={this.options.queryAll}
          onChange={(_, checked) => this.handleOptionsChange('queryAll', checked)}
        />
      </>
    );
  }

  _renderCustomPages_(renderProps) {
    return (
      <ModalPage
        title="Filters manager"
        key={'Page Filter manager'}
        isOpen={this.showFilterManager}
        onClose={this.handleApplyFilters}
        component={this.filterManagerComponent}
        helpLink={links.filterManagerHelp}
      />
    );
  }


  // To prevent remounting of FilterManager when this.sourceFields are changed 
  // because of reference equality check in React, save it as a member method in the class
  filterManagerComponent = () => {
    return <FilterManager
      key={'Filter manager'}
      fields={this.fields}
      disabled={this._readOnly_}
      filters={this.filters}
      onApply={this.handleApplyFilters}
      handlers={{
        onFieldsFilterApplied: this.handleFieldsFilterApplied,
        onLoadFields: this.loadFields,
        onRenderFieldTitle: this.handleRenderFieldTitle,
      }}
    />
  }


  @action handleSelectObject(objectName) {
    runShortAction(async () => {
      this.selectedFieldsIds = [];
      this.expandedFieldsIds = [];
      this.fieldsFilter = '';
      this.selectedObjectName = objectName;
      this.filters = [];
      this.sourceFields = await this.loadRelationship({ referencesTo: [objectName] });

      this.buildQuery();
    });
  }

  @action handleApplyFilters = (filters) => {
    this.showFilterManager = false;

    if (filters) {
      this.filters = filters;
      this.buildQuery();
    }
  }

  @action handleFieldsSelected = (areSelected, items, allSelectedItems) => {
    this.selectedFieldsIds = allSelectedItems;
    this.buildQuery();
  }

  @action handleFieldsCollapsed = async (areCollapsed, items, itemsToLoad, allExpandedItems) => {
    this.expandedFieldsIds = [...allExpandedItems, ...(await this.loadFields(itemsToLoad))];
  }

  @action handleFieldsFilterChanged = (filter) => {
    this.fieldsFilter = filter;
  }

  @action handlePreviewQuery = async () => {
    await runShortAction(async () => {
      this.queryPreview = await this.props.officeStore.substituteQueryOnActiveWorksheet(this.query, this.type);
      this.isQueryPreviewValid = this.queryPreview && this.soql.isQueryValid?.(this.queryPreview);
    });
  }

  @action handleOpenFiltersManager = () => {
    this.showFilterManager = true;
  }

  async fetchObjects() {
    await this.props.officeStore.fetchObjects();
  }

  async prepareQuery() {
    let queryOrPreview = this.query;
    if (!this.isValidQuery) {
      queryOrPreview = this.queryPreview;
    }
    const { columns, objectName } = await this.parseQuery(queryOrPreview);
    return { objectName, fields: columns.map(x => ({ name: x.name })) };
  }

  buildQuery = () => {
    if (!this.selectedObjectName) { return }

    const { getComposedField, composeQuery } = this.soql;

    let fields = this.sourceFields.length ? [this.sourceFields[0].name] : [];
    if (this.selectedFieldsIds.length) {
      fields = getItems(this.sourceFields, this.selectedFieldsIds).map(x => x.fullName);
    }

    const ast = {
      fields: fields.map(i => getComposedField(i)),
      sObject: this.selectedObjectName,
      where: this.filters.length && Object.create(null),
    };

    const replaces = []; // Substitutions workaround

    this.filters.reduce((out, filter) => {
      const { replace, ...left } = createFilterNode(filter, this.fields);
      replace && replaces.push(replace);
      const node = { left };
      if (!out.left) { return Object.assign(out, node) }
      return Object.assign(out, { right: node, operator: filter.join.toUpperCase() }), node;
    }, ast.where);

    // console.log(ast);
    this.query = replaces.reduce((out, { from, to }) => out.replace(from, to), composeQuery(ast));
    this.setDirty();
  }

  @action loadFields = async (idsToLoad) => {
    if (idsToLoad.length) {
      updateItems(this.sourceFields, idsToLoad.map(id => ({ id, isLoading: true })));

      const loaded = await Promise.all(idsToLoad.map(async (id) => {
        const descendants = await this.loadRelationship(getItem(this.sourceFields, id));
        return { descendants, id };
      }));

      updateItems(this.sourceFields, loaded.map(x => ({ id: x.id, isLoading: false, descendants: x.descendants })));
    }
    return idsToLoad;
  }

  handleFieldsFilterApplied = (field, filter) => {
    return includesParttern(this.options.showLabels ? field.label : field.name, filter);
  }

  toSourceFields = (rawFields, parent) => {
    // console.log('Soql::toSourceFields: ', parent, rawFields);

    const nestingDepth = (parent.nestingDepth ?? 0) + 1;
    const parentRelationshipNames = parent.parentRelationshipNames
      ? [...parent.parentRelationshipNames, parent.rawField.relationshipName]
      : [];

    return rawFields.map((rawField, index) => {
      const id = makeId(index, parent.id);
      const { name, label } = rawField;
      const fullName = toFullName(parentRelationshipNames, name);
      const fullLabel = toFullLabel(parentRelationshipNames, label);
      const canHaveDescendants = nestingDepth < Data.MaxRelationshipDepth && isRelationshipField(rawField);
      const isPolymorphic = isPolymorphicField(rawField);

      const baseObject = {
        isLoading: false, parentRelationshipNames, name, label,
        fullName, fullLabel, rawField, nestingDepth,
      };

      const descendants = isPolymorphic && canHaveDescendants
        ? rawField.referenceTo.map((referenceTo, referenceIndex) => ({
          ...baseObject,
          id: makeId(referenceIndex, id),
          isSelectable: false,
          canHaveDescendants: true,
          descendants: [],
          referencesTo: [referenceTo],
          isPolymorphicChild: true,
        }))
        : [];

      return {
        ...baseObject,
        id,
        isPolymorphic,
        isSelectable: true,
        canHaveDescendants,
        descendants,
        referencesTo: canHaveDescendants ? rawField.referenceTo : [],
      };
    });
  }

  handleTestQuery = async () => {
    await this.parseQuery(this.fakeSubstitutedQuery.query);
  }

  fetchObjectData = async (objectName) => {
    return this.props.officeStore.fetchObjectData({ objectName });
  }

  parseQuery = async (query) => {
    const options = ParsingOption.Fields | ParsingOption.Filters | ParsingOption.AllowUnknownObjects;
    return await this.props.salesforceStore.parseSoqlQuery(query, options);
  }

  parseAndLoadQuery = async (query) => {
    let queryOrPreview = query;
    if (!this.isValidQuery && this.fakeSubstitutedQuery.hasSubstitutions) {
      await this.handlePreviewQuery();
      queryOrPreview = this.queryPreview;
    }
    const { columns, filters, objectName, ast } = await this.parseQuery(queryOrPreview);

    //to not display the field tree
    if (ast.fields?.some(field => isFieldsFunction(field))) {
      return { objectName, sourceFields: [], usedFields: [] };
    }

    const root = { descendants: [] };

    const getField = (relationships) => {
      let parent = root;
      for (const relationship of relationships) {
        const { fieldIndex, rawFields, type } = relationship;
        if (!parent.descendants.length) {
          parent.descendants = this.toSourceFields(rawFields, parent);
        }

        const field = parent.descendants[fieldIndex];
        if (type === RelationshipType.Field) {
          return field;
        }

        parent = field;
        if (field.isPolymorphic) {
          parent = parent.descendants.find(x => x.referencesTo[0] === relationship.referenceTo);
        }
      }
    }

    const usedFields = columns.map(({ relationships }) => getField(relationships).id);
    // Just load fields tree
    filters.expressions?.forEach(filter => getField(filter.fieldRelationships));

    return { objectName, sourceFields: root.descendants, usedFields };
  }

  @action loadRelationship = async (parent) => {
    const { fields } = await this.fetchObjectData(parent.referencesTo[0]);
    return this.toSourceFields(fields, parent);
  }

  handleRenderFieldTitle = (item, nestingDepth) => {
    const { originalItem, isActive } = item;
    const { title, tooltip } = originalItem;

    return (
      <ButtonTooltip content={tooltip} styles={{ root: { width: 'auto' } }} >
        <span className={classes(classNames.fieldText, { [classNames.fieldTextInactive]: !isActive })}>{title}</span>
      </ButtonTooltip>
    );
  }
}